import { useEffect, useState } from 'react'
import { useLoaderData, useNavigate } from 'react-router-dom'
import {
  ActivityHistoryModel,
  ProfileActivityHistoryPair,
  ActivityModel,
} from 'types'
import PageAppBar from 'components/PageAppBar'
import {
  Box,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Slider,
  Typography,
  CircularProgress,
} from '@mui/material'
import axios from 'axios'
import { base_url } from 'env'
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker'
import dayjs from 'dayjs'

const RatingSlider = ({
  label,
  value,
  onChange,
}: {
  label: string
  value: number
  onChange: (event: Event, value: number | number[]) => void
}) => (
  <Box sx={{ width: '100%' }}>
    <Typography gutterBottom>{label}</Typography>
    <Slider
      value={value}
      onChange={onChange}
      step={1}
      marks
      min={1}
      max={5}
      valueLabelDisplay="auto"
    />
  </Box>
)

export default function ActivityHistoryEditPage() {
  const { profile, history } = useLoaderData() as ProfileActivityHistoryPair
  const navigate = useNavigate()

  const [activityHistory, setActivityHistory] =
    useState<ActivityHistoryModel>(history)
  const [activities, setActivities] = useState<ActivityModel[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    const token = localStorage.getItem('token')
    if (!token) return

    axios
      .get(`${base_url}/activity/`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        setActivities(res.data)
      })
      .catch((err) => console.error('Failed to load activities', err))
      .finally(() => setIsLoading(false))
  }, [])

  const handleDateChange = (newDate: dayjs.Dayjs | null) => {
    if (newDate) {
      setActivityHistory({ ...activityHistory, date: newDate.unix() })
    }
  }

  const handleSubmit = () => {
    if (!activityHistory.activityId) {
      alert('Veuillez sélectionner une activité.')
      return
    }
    setIsSaving(true)
    const token = localStorage.getItem('token')

    // On s'assure que l'ID n'est pas dans le payload du PUT
    const { id, ...payload } = activityHistory

    axios
      .put(`${base_url}/profile/${profile.id}/activity/${id}`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(() => {
        alert('Modifications enregistrées avec succès !')
        navigate(`/profile/${profile.id}/activity/${id}`) // Navigue vers la page de preview
      })
      .catch((err) => {
        console.error('Failed to save activity history', err)
        alert("L'enregistrement a échoué.")
      })
      .finally(() => setIsSaving(false))
  }

  if (isLoading) {
    return (
      <PageAppBar pageName="Chargement...">
        <CircularProgress />
      </PageAppBar>
    )
  }

  return (
    <PageAppBar pageName={`Modifier une activité pour ${profile.firstName}`}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '1.5rem',
          alignItems: 'center',
          padding: '2rem',
          maxWidth: '600px',
          margin: '0 auto',
        }}
      >
        <Typography variant="h5">Modifier l'enregistrement</Typography>

        <FormControl fullWidth required>
          <InputLabel id="activity-select-label">Activité</InputLabel>
          <Select
            labelId="activity-select-label"
            value={activityHistory.activityId}
            label="Activité"
            onChange={(e) =>
              setActivityHistory({
                ...activityHistory,
                activityId: e.target.value,
              })
            }
          >
            {activities.map((act) => (
              <MenuItem key={act.id} value={act.id}>
                {act.title}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <DateTimePicker
          label="Date et heure de l'activité"
          value={dayjs(Number(activityHistory.date) * 1000)}
          onChange={handleDateChange}
        />

        <TextField
          fullWidth
          label="Durée (en minutes)"
          type="number"
          value={activityHistory.length}
          onChange={(e) =>
            setActivityHistory({
              ...activityHistory,
              length: Number(e.target.value),
            })
          }
        />

        <TextField
          fullWidth
          label="Points Obtenus"
          type="number"
          value={activityHistory.giftedPoints}
          onChange={(e) =>
            setActivityHistory({
              ...activityHistory,
              giftedPoints: Number(e.target.value),
            })
          }
        />

        <Typography variant="h6" sx={{ mt: 2 }}>
          Évaluations
        </Typography>

        <RatingSlider
          label="Évaluation de la durée"
          value={activityHistory.ratingLength}
          onChange={(e, v) =>
            setActivityHistory({
              ...activityHistory,
              ratingLength: v as number,
            })
          }
        />
        <RatingSlider
          label="Évaluation de la difficulté"
          value={activityHistory.ratingDifficulty}
          onChange={(e, v) =>
            setActivityHistory({
              ...activityHistory,
              ratingDifficulty: v as number,
            })
          }
        />
        <RatingSlider
          label="Niveau d'appréciation"
          value={activityHistory.ratingEnjoyed}
          onChange={(e, v) =>
            setActivityHistory({
              ...activityHistory,
              ratingEnjoyed: v as number,
            })
          }
        />
        <RatingSlider
          label="Niveau de concentration"
          value={activityHistory.ratingFocused}
          onChange={(e, v) =>
            setActivityHistory({
              ...activityHistory,
              ratingFocused: v as number,
            })
          }
        />

        <Button
          variant="contained"
          color="success"
          onClick={handleSubmit}
          disabled={isSaving}
          sx={{ mt: 3, minWidth: 150 }}
        >
          {isSaving ? <CircularProgress size={24} /> : 'Enregistrer'}
        </Button>
      </Box>
    </PageAppBar>
  )
}
